/**
 * Goal Progress's Y axis: where the dollar (or count, or %) labels go, and how
 * they read.
 *
 * Recharts' own "auto" ticks divide whatever the domain happens to be into
 * equal parts, so a goal of $300,000 with 12% headroom on top came out as
 * $336,000 / $170,000 / $85,000 — accurate, and unreadable. Kelvin's ask
 * (2026-09-29): round steps, "like 5k 10k 15k", and a choice of what the gap
 * is.
 *
 *   • Auto (the default, no `yStep` stored) — a 1 / 2 / 2.5 / 5 × 10ⁿ step,
 *     whichever puts the top label closest above the highest thing on the
 *     chart in no more than AUTO_MAX_INTERVALS gaps.
 *   • A fixed step the admin picked ($5,000, 10 jobs, 5%) — drawn EXACTLY,
 *     for as many labels as the chart's real height has room for (one per
 *     LABEL_PX). Only a step genuinely too fine to print (every $1,000 up a
 *     $300,000 axis is 300 labels) is thinned, to every 2nd / 5th / 10th
 *     multiple — still the admin's multiples, never overlapping text.
 *
 * The first cut thinned anything past 8 gaps, which on a $300k running total
 * turned $1k, $5k, $10k, $25k and $50k into the SAME $50k axis — five
 * options that visibly did nothing. The cap is now the pixel height, and the
 * editor's quick picks come from stepPresets(), sized to the card's own goal
 * so every one of them draws a different axis.
 *
 * Pure, no recharts — the tests cover it without rendering a chart.
 */

/* Room above the highest point, as a share of it: enough that the end dot,
   the "Pace" caption and the goal pill are not sitting on the top gridline. */
const HEADROOM = 1.05;
const AUTO_MAX_INTERVALS = 6;
/* Vertical room one 11px label needs to stay clear of the next. */
export const LABEL_PX = 15;
/* Before the chart has measured itself (first paint, tests). */
const DEFAULT_MAX_INTERVALS = 12;
const NICE = [1, 2, 2.5, 5];
const THIN = [1, 2, 5];

/* Floating-point dust off a multiple (0.1 * 3 = 0.30000000000000004). */
const clean = (n) => Number(n.toPrecision(12));

/** A usable step out of whatever was stored: a finite number above zero, or null (Auto). */
export const parseStep = (raw) => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/* The auto step: of every nice step, the one whose top label lands closest
   above `need` in at most AUTO_MAX_INTERVALS gaps. A tie goes to MORE labels:
   $0 / $5k / … / $30k reads better than $0 / $10k / $20k / $30k, and a lone
   $0 / $200k / $400k says almost nothing. */
const autoStep = (need, cap) => {
  const mag = 10 ** Math.floor(Math.log10(need / cap));
  let best = null;
  [mag, mag * 10].forEach((m) => NICE.forEach((f) => {
    const step = clean(f * m);
    const count = Math.ceil(need / step - 1e-9);
    if (count > cap) return;
    const top = count * step;
    if (!best || top < best.top || (top === best.top && count > best.count)) best = { step, top, count };
  }));
  return best ? best.step : clean(10 * mag);
};

/* A fixed step as-is when it fits, else thinned to every 2nd / 5th / 10th /
   20th… multiple — the first that fits in `cap` gaps. */
const thinStep = (step, need, cap) => {
  for (let mag = 1; ; mag *= 10) {
    const k = THIN.find((t) => Math.ceil(need / (step * t * mag) - 1e-9) <= cap);
    if (k) return clean(step * k * mag);
  }
};

/** How many gaps a plot `plotHeight` px tall can label without overlap. */
export const maxIntervalsFor = (plotHeight) => (
  Number(plotHeight) > 0 ? Math.max(3, Math.floor(plotHeight / LABEL_PX)) : DEFAULT_MAX_INTERVALS
);

/**
 * The labels up the axis, from 0 to the first one at or above `max` (plus
 * headroom). `step` is the admin's chosen gap, or null for Auto; `plotHeight`
 * the plot's measured height in px, which is what bounds a fixed step.
 * Returns `{ ticks, top, step }` — `top` is the axis's upper bound, `step` the
 * gap actually used (after any thinning).
 */
export const axisTicks = ({ max, step, plotHeight }) => {
  const fixed = parseStep(step);
  const peak = Number.isFinite(max) && max > 0 ? max : 0;
  /* Nothing on the chart yet — one gap, so a flat line still has an axis. */
  if (!peak) {
    const only = fixed || 1;
    return { ticks: [0, only], top: only, step: only };
  }
  /* No headroom under a fixed step: the admin chose the grid, and padding the
     top pushed a $25k step up a $300k goal from 12 labels to 13 — past what
     the plot fits, so it got thinned into looking like $50k. The top label is
     still the first multiple at or above the peak. */
  const need = fixed ? peak : peak * HEADROOM;
  const cap = maxIntervalsFor(plotHeight);
  const used = fixed ? thinStep(fixed, need, cap) : autoStep(need, Math.min(AUTO_MAX_INTERVALS, cap));
  const count = Math.max(1, Math.ceil(need / used - 1e-9));
  const ticks = Array.from({ length: count + 1 }, (_, i) => clean(i * used));
  return { ticks, top: ticks[ticks.length - 1], step: used };
};

/**
 * The editor's quick picks: the nice steps that give this card's chart between
 * 2.5 and 12 labels, so each one visibly changes the axis. Sized off what the
 * chart climbs to — the goal on a running total; on a day-by-day chart, about
 * twice the per-day pace (a good day's bar), taken over ~21 working days.
 * Returns [] when there's no goal to size from — the editor falls back to a
 * fixed list.
 */
export const stepPresets = ({ goal, view }) => {
  const g = Number(goal);
  if (!Number.isFinite(g) || g <= 0) return [];
  const ref = view === "daily" ? (g / 21) * 2 : g;
  const out = [];
  const lo = Math.floor(Math.log10(ref / 12));
  for (let e = lo; e <= lo + 2; e += 1) {
    NICE.forEach((f) => {
      const st = clean(f * 10 ** e);
      const n = ref / st;
      if (n >= 2.5 && n <= 12 && st >= 0.01) out.push(st);
    });
  }
  return out.slice(0, 4);
};

/* 12.5 → "12.5", 12.50 → "12.5", 12 → "12". Two decimals is as fine as a
   thinned or typed step ever needs ($1.25M). */
const trim = (n) => String(Math.round(n * 100) / 100);

/** The axis label: "$15k", "$1.25M", "250", "40%" — short, so a big goal's
 *  labels fit the axis without a wider gutter. The tooltip keeps full figures. */
export const formatAxis = (value, kind) => {
  const v = Number(value) || 0;
  if (kind === "%") return `${trim(v)}%`;
  const abs = Math.abs(v);
  let body;
  if (abs >= 1e6) body = `${trim(v / 1e6)}M`;
  else if (abs >= 1e3) body = `${trim(v / 1e3)}k`;
  else body = trim(v);
  return kind === "$" ? `$${body}` : body;
};
